import cx from "classnames";
import { useField, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton } from "@gaming/xds-react-js";

import { siteLocationPrefix } from "../../constants";
import {
  useProgramBackgroundColorPicker,
  useProgramColorPicker,
} from "../../helpers/useProgramColorPicker";
import { useStrings } from "../../localization/localizationHelpers";
import { StudioNameValidationResponse } from "../../server/partnerCenter/models/studioNameValidation";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import { FieldStateProps } from "../FieldHeader";
import * as styles from "./StudioNameValidator.module.css";

const verifyPartnerCenterStudioNameUrl = `${siteLocationPrefix}/partnercenter/verifyPartnerCenterStudioName?studioName={0}`;

type StudioNameValidatorProps = Readonly<{
  name: string;
  studioName: string;
}> &
  FieldStateProps;

export const StudioNameValidator = ({ name, studioName, disabled }: StudioNameValidatorProps) => {
  const [
    studioNameValidatorButtonText,
    studioNameValidatorErrorText,
    studioNameValidatorSuccessText,
    studioNameValidatorLoadingText,
  ] = useStrings(
    "studioNameValidatorButtonText",
    "studioNameValidatorErrorText",
    "studioNameValidatorSuccessText",
    "studioNameValidatorLoadingText"
  );
  const [studioNameField] = useField(studioName);
  const [validatedStudioNameField] = useField(name);
  const { setFieldValue } = useFormikContext();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const isButtonDisabled = !studioNameField.value || isValidating;
  const [validationResult, setValidationResult] = useState<boolean | null>(null);
  const [validatedStudioName, setValidatedStudioName] = useState<string | null>(null);

  const programColorStyle = useProgramColorPicker();
  const backgroundColorStyle = useProgramBackgroundColorPicker(!isButtonDisabled);

  useEffect(() => {
    // Initial load check to compare previously validated studio name with current field value
    if (
      isInitialLoad &&
      studioNameField.value &&
      studioNameField.value === validatedStudioNameField.value
    ) {
      setValidationResult(true);
      setValidatedStudioName(validatedStudioNameField.value);
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, studioNameField.value, validatedStudioNameField.value]);

  useEffect(() => {
    // Reset validation result when studio name field changes
    if (
      validatedStudioNameField.value &&
      studioNameField.value !== validatedStudioNameField.value
    ) {
      setValidatedStudioName(null);
      setValidationResult(null);
      setFieldValue(name, null);
    }
  }, [name, setFieldValue, studioNameField, validatedStudioName, validatedStudioNameField]);

  const onValidateStudioName = async () => {
    const nameToValidate = studioNameField.value;
    if (!nameToValidate) {
      return;
    }

    setIsValidating(true);
    const response = await fetch(verifyPartnerCenterStudioNameUrl.replace("{0}", nameToValidate), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const contentData = (await response.json()) as StudioNameValidationResponse;

    setValidationResult(contentData.isStudioNameAvailable);
    setValidatedStudioName(nameToValidate);
    setFieldValue(name, contentData.isStudioNameAvailable ? nameToValidate : null);
    setIsValidating(false);
  };

  if (disabled) {
    return null; // Don't render the validator if all fields are disabled
  }

  return (
    <Row className={cx(commonStyles.container, styles.validatorRow)}>
      <Column className={styles.validatorColumn}>
        {studioNameField.value !== validatedStudioName && (
          <BasicButton
            as={"button"}
            onClick={onValidateStudioName}
            className={cx(backgroundColorStyle, commonStyles.primaryButton)}
            textDecorationUnderline={true}
            disabled={isButtonDisabled}
          >
            {isValidating ? studioNameValidatorLoadingText : studioNameValidatorButtonText}
          </BasicButton>
        )}
        {!isValidating &&
          validationResult !== null &&
          studioNameField.value === validatedStudioName && (
            <span
              className={cx(
                styles.responseText,
                validationResult ? programColorStyle : styles.errorText
              )}
            >
              {validationResult ? studioNameValidatorSuccessText : studioNameValidatorErrorText}
            </span>
          )}
      </Column>
    </Row>
  );
};
